import React from 'react';
import PdfPreviewModal from '../components/PdfPreviewModal';
import PdfA4Page from './PdfA4Page';
import PdfTable from './PdfTable';
import PdfMeasureTemplate from './PdfMeasureTemplate';
import { A4 } from './pdfEngine';

/**
 * Drop-in component that renders everything the Shared PDF Engine needs:
 *  1. hidden measurement template (dynamic pagination)
 *  2. multi-page preview modal (identical to print output)
 *  3. off-screen print pages (captured by the engine)
 *  4. single-record preview modal + off-screen single print page
 *
 * Pages supply data/config only — no page-specific PDF code.
 *
 * Props:
 *  - pdf: value returned by usePdfExport()
 *  - title: report title (shown in PDF header)
 *  - columns: shared column config (see PdfTable)
 *  - rows: full data set (for measurement)
 *  - filtersSummary?: string shown under the title
 *  - renderRow?: custom <tr/> renderer (row, globalIndex, localIndex)
 *  - singleTitle?: title for single-record PDFs (defaults to `title`)
 *  - renderSingle?: (record) => JSX — content of the single-record page
 *  - previewTitle?: modal title
 */
const OffScreen = ({ children }) => (
  <div
    aria-hidden="true"
    style={{
      position: 'absolute',
      left: '-9999px',
      top: 0,
      pointerEvents: 'none',
    }}
  >
    {children}
  </div>
);

const PdfExportManager = ({
  pdf,
  title,
  columns,
  rows,
  filtersSummary,
  renderRow,
  singleTitle,
  renderSingle,
  previewTitle,
  numberingOffset = 0, // e.g. (current_page - 1) * per_page for continuous numbering
}) => {
  const {
    idPrefix,
    isRTL,
    isPreviewOpen,
    isSinglePreviewOpen,
    isGeneratingPdf,
    pdfChunks,
    singleRecord,
    printMode,
    printAll,
    printSingle,
    closePreview,
    closeSinglePreview,
  } = pdf;

  const renderChunkPage = (chunk, pageIndex, withId) => (
    <PdfA4Page
      key={pageIndex}
      id={withId ? `${idPrefix}-pdf-page-${pageIndex}` : undefined}
      isRTL={isRTL}
      title={title}
      filtersSummary={filtersSummary}
    >
      <PdfTable
        columns={columns}
        rows={chunk.items}
        startIndex={numberingOffset + chunk.startIndex}
        isRTL={isRTL}
        renderRow={renderRow}
      />
    </PdfA4Page>
  );

  const renderSinglePage = withId =>
    singleRecord ? (
      <PdfA4Page
        id={withId ? `${idPrefix}-pdf-single` : undefined}
        isRTL={isRTL}
        title={singleTitle || title}
      >
        {renderSingle ? (
          renderSingle(singleRecord)
        ) : (
          <PdfTable
            columns={columns}
            rows={[singleRecord]}
            startIndex={0}
            isRTL={isRTL}
            renderRow={renderRow}
          />
        )}
      </PdfA4Page>
    ) : null;

  return (
    <>
      {/* 1. Measurement template — always mounted so pagination is instant */}
      <PdfMeasureTemplate
        idPrefix={idPrefix}
        title={title}
        columns={columns}
        rows={rows}
        renderRow={renderRow}
      />

      {/* 2. Multi-page preview (same components as print output) */}
      <PdfPreviewModal
        isOpen={isPreviewOpen}
        title={previewTitle || title}
        onClose={closePreview}
        onPrint={() => printAll(rows)}
        isGeneratingPdf={isGeneratingPdf}
        contentWidth={`${A4.WIDTH_PX}px`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {pdfChunks.map((chunk, i) => renderChunkPage(chunk, i, false))}
        </div>
      </PdfPreviewModal>

      {/* 3. Off-screen print pages */}
      {printMode === 'all' && (
        <OffScreen>
          {pdfChunks.map((chunk, i) => renderChunkPage(chunk, i, true))}
        </OffScreen>
      )}

      {/* 4. Single-record preview + off-screen print page */}
      <PdfPreviewModal
        isOpen={isSinglePreviewOpen}
        title={singleTitle || title}
        onClose={closeSinglePreview}
        onPrint={() => printSingle(singleRecord)}
        isGeneratingPdf={isGeneratingPdf}
        contentWidth={`${A4.WIDTH_PX}px`}
      >
        {renderSinglePage(false)}
      </PdfPreviewModal>

      {printMode === 'single' && <OffScreen>{renderSinglePage(true)}</OffScreen>}
    </>
  );
};

export default PdfExportManager;
